import classNames from 'classnames';
import QaRolesCustomSelectField from 'components/QaRolesCustomSelectField';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import useOptions from 'router/subrouters/DataRoom/subrouters/Members/hooks/useOptions';
import { capitalizeName } from 'utils';

import './ManageRequests.styles.scss';

type ManageRequestsProps = {
  className?: string;
  isBuySide: boolean;
  member: DataRoomMember;
  updateMember: (member: DataRoomMember) => void;
  removeMember: (memberId: string) => void;
};

const ManageRequests: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageRequestsProps
> = ({ isBuySide, member, updateMember, removeMember }, ref) => {
  const [isLoading, setIsLoading] = useState(false);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { t } = useTranslation();

  const name =
    capitalizeName(member?.fullName) || member?.email || t('unknownUser');

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('manage-requests', classNames);
  const { sellerRoleOptions, sellerQaRole, buyerRoleOptions, buyerQaRole } =
    useOptions();

  const initialData = useMemo(
    () =>
      member
        ? {
            qaRole: member.qaRole,
            role: member.role,
          }
        : {},
    [member],
  );

  const onSubmit = useCallback(
    async (values: Partial<DataRoomMember>) => {
      try {
        setIsLoading(true);
        const { data } = await dataRoomApi.members.acceptRequest(
          member?._id,
          values,
        );

        updateMember(data);

        drawer?.setOpen(false);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [drawer, member, popServerError, updateMember],
  );

  const declineRequest = useCallback(async () => {
    try {
      setIsLoading(true);
      await dataRoomApi.members.declineRequest(member?._id);

      removeMember(member?._id);

      drawer?.setOpen(false);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [drawer, member?._id, popServerError, removeMember]);

  return (
    <SideDrawer ref={drawerRef} className={classes}>
      <p className="drawer-title">
        {t('memberIsRequestingAccess', {
          name,
        })}
      </p>

      <Form
        initialValues={initialData}
        onSubmit={onSubmit}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            {isLoading && <Loader />}

            <QaRolesCustomSelectField
              name="role"
              label={t('documentsRole')}
              options={isBuySide ? buyerRoleOptions : sellerRoleOptions}
              renderAsPortal
            />

            <QaRolesCustomSelectField
              name="qaRole"
              label={t('qaRole')}
              options={isBuySide ? buyerQaRole : sellerQaRole}
              renderAsPortal
            />

            <div className="manage-requests__actions">
              <Button type="submit" disabled={isLoading}>
                {t('approve')}
              </Button>

              <Button
                type="button"
                disabled={isLoading}
                variant="outline"
                onClick={declineRequest}
              >
                {t('decline')}
              </Button>
            </div>
          </form>
        )}
      />
    </SideDrawer>
  );
};

export default forwardRef(ManageRequests);
